import DOMPurify from "dompurify";

/**
 * @description - Sanitize the html content
 * @param {string} htmlContent - HTML string
 */
export const cleanSectionHtml = (htmlContent: string): string => {
  try {
    const htmlSanitized = DOMPurify.sanitize(htmlContent, {
      ADD_TAGS: ["params"],
      USE_PROFILES: { html: true },
    });
    return htmlSanitized;
  } catch (error) {
    return htmlContent;
  }
};

/**
 * @description - Remove empty nodes except the td
 * @param {string} htmlContent - HTML string
 */
export const purifyHtmlContent = (htmlContent: string): string => {
  try {
    // Add a hook to remove empty nodes
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
      if (node.tagName == "TD" && !node.textContent) {
        // fill empty tds with non breaking space for visibility purpose in the editor
        node.innerHTML = "&nbsp;";
      } else if (node.tagName == "PARAMS") {
        if (node.childNodes.length > 1 && node.textContent) {
          node.innerHTML = node.textContent;
        }
      } else {
        // Remove the node in case it is empty
        if (!node.hasChildNodes() && !node.textContent) {
          node.remove();
        }
      }
    });
    const purifiedContent = DOMPurify.sanitize(htmlContent, {
      ADD_TAGS: ["params"],
    });
    return purifiedContent;
  } catch (error) {
    return htmlContent;
  }
};

/**
 * @description - Remove empty nodes except the td
 * @param {string} htmlContent - HTML string
 */
export const removeEmptyP = (htmlContent: string): string => {
  try {
    // Add a hook to remove empty nodes
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
      if (node.tagName == "SPAN" && !node.hasChildNodes()) {
        node.remove();
      } else if (node.tagName == "P" && !node.hasChildNodes()) {
        node.remove();
      }
    });
    const purifiedContent = DOMPurify.sanitize(htmlContent.trim(), {
      ADD_TAGS: ["params"],
      ADD_ATTR: ["repeatingadapterid", "repeatable", "isrepeating"],
    });
    return purifiedContent;
  } catch (error) {
    return htmlContent;
  }
};

/**
 * @description - Fill empty cell (td) with nbsp
 * @param {string} htmlContent - HTML string
 */
export const fillEmptyCells = (htmlContent: string): string => {
  try {
    // Add a hook to remove empty nodes
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
      if (node.tagName == "TD" && !node.textContent && !node.hasChildNodes()) {
        node.innerHTML = "&nbsp;";
      }
      if (node.tagName == "PARAMS") {
        node.removeAttribute("suppresscontenteditablewarning");
        node.removeAttribute("contenteditable");
      }
    });

    const purifiedContent = DOMPurify.sanitize(htmlContent.trim(), {
      ADD_TAGS: ["params"],
      ADD_ATTR: ["repeatingadapterid", "repeatable", "isrepeating"],
    });
    return purifiedContent;
  } catch (error) {
    return htmlContent;
  }
};

/**
 * @description - Remove unwanted tags and attributes from text content
 * @param {string} textContent - HTML text content string
 */
export const purifyTextContent = (textContent: string): string => {
  try {
    // Add a hook to remove empty nodes
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
      if (node.tagName == "TD" && !node.textContent && !node.hasChildNodes()) {
        node.innerHTML = "&nbsp;";
      }
      if (node.tagName == "PARAMS") {
        node.removeAttribute("suppresscontenteditablewarning");
        node.removeAttribute("contenteditable");
        node.removeAttribute("isrepeating");
      }
    });

    const purifiedContent = DOMPurify.sanitize(textContent.trim(), {
      ADD_TAGS: ["params"],
      ADD_ATTR: ["repeatingadapterid", "repeatable", "isrepeating"],
    });
    return purifiedContent;
  } catch (error) {
    return textContent;
  }
};

/**
 * @description - Remove unwanted tags and attributes from SMS content and add br tags for line breaks
 * @param {string} content - HTML content of the SMS
 */
export const purifySMSContent = (content: string) => {
  try {
    // Add a hook to remove empty nodes
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
      if (node.tagName == "P") {
        const hasContent = node.textContent?.trim()?.length;
        if (!hasContent && !node.hasChildNodes()) {
          node.remove();
        }
      }
      if (node.tagName == "PARAMS") {
        node.removeAttribute("suppresscontenteditablewarning");
        node.removeAttribute("contenteditable");
        node.removeAttribute("isrepeating");
      }
      if (node.tagName === "P") {
        const newNode = document.createElement("span");
        newNode.innerHTML = node.innerHTML;
        node?.parentNode?.replaceChild(newNode, node);
      }
    });

    const purifiedContent = DOMPurify.sanitize(content.trim(), {
      USE_PROFILES: { html: true },
      ADD_TAGS: ["params"],
      ADD_ATTR: ["repeatingadapterid", "repeatable", "isrepeating"],
      ALLOWED_TAGS: ["br", "div", "span", "params", "p"],
    });
    return purifiedContent;
  } catch (error) {
    return content;
  }
};
